package pl.decks.personal.components.widgets.progression

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.percent
import pl.decks.personal.components.widgets.progression.RampingProgressionBarStylesheet.Vars
import kotlin.math.ceil
import kotlin.math.pow
import kotlin.time.Duration.Companion.milliseconds


@Composable
fun RampingProgressionBar(
    progress: Float,
    minProgress: Float = 0f,
    maxProgress: Float = 100f,
    modifier: Modifier = Modifier,
    rampUpDurationMs: Int = 1700,
    formatter: (Float, Float, Float) -> String = { v, _, _ -> v.toString() },
) = Box(
    RampingProgressionBarStylesheet.Bar.toModifier().then(modifier),
    contentAlignment = Alignment.CenterStart,
) {
    val targetProgressNormalized = (progress - minProgress) / (maxProgress - minProgress)
    var animatedProgress by remember { mutableStateOf(targetProgressNormalized) }
    val displayProgress = animatedProgress * (maxProgress - minProgress) + minProgress
    EaseOnLaunch(progress, animationDurationMs = rampUpDurationMs) { animatedProgress = it * targetProgressNormalized }

    Box(RampingProgressionBarStylesheet.GuideLine.toModifier())
    ProgressionBar(animatedProgress, text = formatter(displayProgress, minProgress, maxProgress))
}

@Composable
private fun ProgressionBar(progress: Float, text: String) = Box(
    modifier = RampingProgressionBarStylesheet.ProgressionCounter.toModifier()
        .setVariable(Vars.progressWidth, clamp(0.percent, progress.times(100).percent, 100.percent)),
    contentAlignment = Alignment.CenterEnd,
) {
    Box(RampingProgressionBarStylesheet.ProgressionLine.toModifier())
    Box(
        modifier = RampingProgressionBarStylesheet.ProgressionText.toModifier(),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(text)
    }
}

@Composable
private fun EaseOnLaunch(
    vararg keys: Any?,
    animationDurationMs: Int,
    frameDurationMs: Int = 16,
    easingFunction: (animFraction: Float) -> Float = { if (it < 0.5) 2 * it.pow(2) else -1 + (4 - 2 * it) * it },
    onFrameFinished: (easing: Float) -> Unit,
) = LaunchedEffect(keys) {
    val frames = ceil(animationDurationMs.toFloat() / frameDurationMs).toInt()
    for (frame in 1..frames) {
        val animationFraction = frame / frames.toFloat()
        val easing = easingFunction(animationFraction)
        onFrameFinished(easing)
        delay(frameDurationMs.toLong().milliseconds)
    }
}
